/**
 * ReviewCountContext — provides the number of mastery topics due for review
 * to any component without triggering duplicate Supabase queries.
 *
 * R2 fix (2026-05-09): surfaced in Hat 2 audit — students who never visit the
 * Progress page have no indicator that review topics are waiting. The count is
 * fetched once here (inside AppShell) and consumed by BottomNav (badge dot)
 * and Home (overflow count) via context, with zero extra DB queries.
 */
import { createContext, useContext, useMemo } from 'react'
import { useMastery } from '@/hooks/useMastery'
import { useAuth }    from '@/context/AuthContext'

interface ReviewCountContextValue {
  dueCount: number   // number of topics whose next_review date is today or past
}

const ReviewCountContext = createContext<ReviewCountContextValue>({ dueCount: 0 })

export function ReviewCountProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuth()
  const gradeLevel        = profile?.grade_level ?? 5

  const { getDueTopics } = useMastery(user?.id ?? '', gradeLevel)

  const dueCount = useMemo(() => getDueTopics().length, [getDueTopics])

  return (
    <ReviewCountContext.Provider value={{ dueCount }}>
      {children}
    </ReviewCountContext.Provider>
  )
}

export function useReviewCount(): ReviewCountContextValue {
  return useContext(ReviewCountContext)
}
